import type { RawThemeConfig } from '@/types/ThemeOptions'

export const darkGray = '#222222'
export const mediumGray = '#555555'
export const lightGray = '#DDE0E5'
export const veryLightGray = '#f7f7f7'

function white(amount) {
  return `rgba(255, 255, 255, ${amount})`
}

const primaryColor = 'var(--primary-color)'
const pageBackgroundColor = 'var(--page-background-color)'
const primaryColorDarker = 'var(--primary-color-darker)'

export const fallback: Partial<RawThemeConfig> = {
  primaryColor: darkGray,
  secondaryColor: darkGray,
  messageColor: darkGray,
  labelColor: [darkGray, 'white'],
  headerBackground: ['white', 'transparent'],
  memberLinksColor: [darkGray, 'white'],
  memberLinksColorHover: [primaryColorDarker, white(0.5)],
  logoHeight: '32px',
  footerBackgroundColor: ['white', white(0.09)],
  footerTextColor: [mediumGray, white(0.6)],
  bodyColor: [mediumGray, white(0.6)],
  headingColor: ['black', white(0.87)],
  pageBackgroundColor: ['white', 'black'],
  headerTopBorder: 'none',
  headerBottomBorder: 'none',
  headerDropShadow: 'none',
  headerHeight: '105px',
  secondaryButtonStyle: 'outline',
  cartBorder: ['1px solid var(--light-gray)', `1px solid ${white(0.1)}`],
  infoColor: '#3b73d8',
  successColor: '#006400',
  warningColor: '#ffa500',
  errorColor: '#eb1c26',
  promoColor: '#9877b0',
  primaryFont: 'proxima-nova, Arial, sans-serif',
  secondaryFont: 'proxima-nova, Arial, sans-serif',
  externalFontsStylesheets: ['https://use.typekit.net/tsu4pxd.css'],
  selectArrowColor: ['var(--primary-color)', 'white'],
  cart: {
    headerBackgroundColor: [darkGray, white(0.13)],
    headerTextColor: white(0.87),
    headerBorder: 'none',
    backgroundColor: ['white', pageBackgroundColor],
    priceBackgroundColor: ['#f7f7f7', white(0.05)],
    dividerColor: [lightGray, white(0.13)],
    removeButtonBackgroundColor: [lightGray, white(0.05)],
  },
  dialog: {
    backgroundColor: ['#F7F9FA', 'var(--page-background-color-lighter)'],
    headerTextColor: ['black', 'white'],
    headerBackground: ['white', white(0.05)],
    headerIconColor: ['#555555', white(0.25)],
    headerBorder: ['1px solid #DDE0E6', 'none'],
  },
  mobileFooter: {
    backgroundColor: ['#ffffff', pageBackgroundColor],
    textColor: ['#545557', white(0.6)],
    cartButtonBackgroundColor: ['#ffffff', pageBackgroundColor],
    cartButtonHoverBackgroundColor: ['#ffffff', pageBackgroundColor],
    cartButtonBorder: ['2px solid #BFC1CA', '2px solid rgba(255, 255, 255, 0.2)'],
    cartButtonIconColor: ['#747981', '#ffffff'],
    cartBadgeBackgroundColor: '#ff0000',
    cartBadgeTextColor: '#ffffff',
    priceTextColor: ['#545557', white(0.87)],
  },
  backButton: {
    iconColor: '#282B37',
    backgroundColor: 'white',
    shadow: '0px 0px 5px rgba(0, 0, 0, 0.4)',
  },
  calendar: {
    backgroundColor: ['rgba(255, 255, 255, 0.1)', 'rgba(34, 36, 51, 0.1)'],
    buttonColor: ['#ffffff', white(0.05)],
    buttonIconColor: ['#282B37', white(0.87)],
    dayTextColor: ['#282B37', white(0.6)],
    selectedDayTextColor: 'var(--primary-contrast-color)',
    todayBackgroundColor: ['#EFF0F5', 'rgba(255,255,255,0.2)'],
    disabledDayTextColor: ['#BFC1CA', white(0.3)],
    divider: ['1px dashed #cccccc', `1px dashed ${white(0.1)}`],
    monthTextColor: ['#282B37', white(0.87)],
    weekdayTextColor: ['#525A6C', white(0.87)],
    legendTextColor: ['#525A6C', white(0.6)],
  },
  steppers3: {
    iconColor: [mediumGray, 'white'],
    border: [`2px solid ${lightGray}`, `2px solid ${white(0.18)}`],
    buttonBackgroundColor: ['white', white(0.05)],
    buttonBackgroundHoverColor: ['white', white(0.15)],
    borderHoverColor: ['#bfc1ca', 'rgba(255, 255, 255, 0.5)'],
    disabledIconColor: ['var(--light-gray)', white(0.2)],
    count: {
      backgroundColor: [lightGray, 'transparent'],
      border: [`2px solid ${lightGray}`, `2px solid ${white(0.18)}`],
    },
  },
  radioButton: {
    borderColor: ['var(--medium-gray)', white(0.18)],
    backgroundColor: ['white', white(0.05)],
    borderWidth: '2px',
    checked: {
      backgroundColor: ['white', 'transparent'],
      borderColor: primaryColor,
    },
  },
  editOrder: {
    sessionBorderColor: ['#dde0e5', white(0.09)],
    ticketBackgroundColor: ['#eff0f5', white(0.05)],
  },
  divider: [`${lightGray} 1px solid`, `${white(0.1)} 1px solid`],
}
